import { ApiResponse } from '@shared/types';
import {
  InventoryDashboardFilterParams,
  InventoryDashboardLocationValueRow,
  InventoryDashboardLowStockRow,
  InventoryDashboardMovementDailyRow,
  InventoryDashboardOverviewRow,
  InventoryDashboardPurchaseDailyRow,
  InventoryDashboardRecentActivityRow,
  InventoryDashboardSnapshot,
} from '../types/inventory';
import type { PaginatedResponse } from '../types/pagination';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapOverviewRow = (
  value: Record<string, unknown>,
): InventoryDashboardOverviewRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  total_items: toNumber(value.total_items),
  active_items: toNumber(value.active_items),
  stock_location_count: toNumber(value.stock_location_count),
  low_stock_item_count: toNumber(value.low_stock_item_count),
  total_quantity_on_hand: toNumber(value.total_quantity_on_hand),
  total_stock_value: toNumber(value.total_stock_value),
  purchase_invoice_count: toNumber(value.purchase_invoice_count),
  total_purchase_value: toNumber(value.total_purchase_value),
  outstanding_payables: toNumber(value.outstanding_payables),
  active_suppliers: toNumber(value.active_suppliers),
  active_recipes: toNumber(value.active_recipes),
});

const mapMovementDailyRow = (
  value: Record<string, unknown>,
): InventoryDashboardMovementDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  movement_count: toNumber(value.movement_count),
  quantity_in: toNumber(value.quantity_in),
  quantity_out: toNumber(value.quantity_out),
  movement_value_in: toNumber(value.movement_value_in),
  movement_value_out: toNumber(value.movement_value_out),
  purchase_receipt_count: toNumber(value.purchase_receipt_count),
  sale_consumption_count: toNumber(value.sale_consumption_count),
  wastage_count: toNumber(value.wastage_count),
  adjustment_count: toNumber(value.adjustment_count),
});

const mapPurchaseDailyRow = (
  value: Record<string, unknown>,
): InventoryDashboardPurchaseDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  invoice_count: toNumber(value.invoice_count),
  payment_count: toNumber(value.payment_count),
  purchase_value: toNumber(value.purchase_value),
  amount_paid: toNumber(value.amount_paid),
  outstanding_value: toNumber(value.outstanding_value),
});

const mapLowStockRow = (
  value: Record<string, unknown>,
): InventoryDashboardLowStockRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  stock_location_id: String(value.stock_location_id || ''),
  stock_location_name: String(value.stock_location_name || ''),
  inventory_item_id: String(value.inventory_item_id || ''),
  item_name: String(value.item_name || ''),
  sku: (value.sku as string | null) ?? null,
  reorder_level: toNumber(value.reorder_level),
  quantity_on_hand: toNumber(value.quantity_on_hand),
  deficit_quantity: toNumber(value.deficit_quantity),
  total_value: toNumber(value.total_value),
});

const mapLocationValueRow = (
  value: Record<string, unknown>,
): InventoryDashboardLocationValueRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  stock_location_id: String(value.stock_location_id || ''),
  stock_location_name: String(value.stock_location_name || ''),
  balance_row_count: toNumber(value.balance_row_count),
  total_quantity_on_hand: toNumber(value.total_quantity_on_hand),
  total_stock_value: toNumber(value.total_stock_value),
});

const mapRecentActivityRow = (
  value: Record<string, unknown>,
): InventoryDashboardRecentActivityRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  activity_type:
    value.activity_type as InventoryDashboardRecentActivityRow['activity_type'],
  activity_id: String(value.activity_id || ''),
  occurred_at: String(value.occurred_at || ''),
  headline: String(value.headline || ''),
  detail: (value.detail as string | null) ?? null,
  reference_type: (value.reference_type as string | null) ?? null,
  reference_id: (value.reference_id as string | null) ?? null,
  quantity: value.quantity === null ? null : toNumber(value.quantity),
  amount: value.amount === null ? null : toNumber(value.amount),
});

const addDays = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatTimestampStart = (date: string) =>
  new Date(`${date}T00:00:00`).toISOString();

const formatTimestampEnd = (date: string) =>
  new Date(`${date}T23:59:59.999`).toISOString();

const fillMovementDailyRows = (
  rows: InventoryDashboardMovementDailyRow[],
  dateFrom: string,
  dateTo: string,
) => {
  const byDate = new Map(rows.map((row) => [row.metric_date, row]));
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  const filled: InventoryDashboardMovementDailyRow[] = [];

  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    const metricDate = formatDateKey(cursor);
    filled.push(
      byDate.get(metricDate) ?? {
        company_id: rows[0]?.company_id ?? 0,
        outlet_id: rows[0]?.outlet_id ?? null,
        metric_date: metricDate,
        movement_count: 0,
        quantity_in: 0,
        quantity_out: 0,
        movement_value_in: 0,
        movement_value_out: 0,
        purchase_receipt_count: 0,
        sale_consumption_count: 0,
        wastage_count: 0,
        adjustment_count: 0,
      },
    );
  }

  return filled;
};

const fillPurchaseDailyRows = (
  rows: InventoryDashboardPurchaseDailyRow[],
  dateFrom: string,
  dateTo: string,
) => {
  const byDate = new Map(rows.map((row) => [row.metric_date, row]));
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  const filled: InventoryDashboardPurchaseDailyRow[] = [];

  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    const metricDate = formatDateKey(cursor);
    filled.push(
      byDate.get(metricDate) ?? {
        company_id: rows[0]?.company_id ?? 0,
        outlet_id: rows[0]?.outlet_id ?? null,
        metric_date: metricDate,
        invoice_count: 0,
        payment_count: 0,
        purchase_value: 0,
        amount_paid: 0,
        outstanding_value: 0,
      },
    );
  }

  return filled;
};

const aggregateOverview = (
  rows: InventoryDashboardOverviewRow[],
): InventoryDashboardOverviewRow | null => {
  if (!rows.length) {
    return null;
  }

  return rows.reduce<InventoryDashboardOverviewRow>(
    (accumulator, row, index) => {
      if (index === 0) {
        return { ...row };
      }

      return {
        company_id: row.company_id,
        outlet_id: null,
        total_items: Math.max(accumulator.total_items, row.total_items),
        active_items: Math.max(accumulator.active_items, row.active_items),
        stock_location_count:
          accumulator.stock_location_count + row.stock_location_count,
        low_stock_item_count:
          accumulator.low_stock_item_count + row.low_stock_item_count,
        total_quantity_on_hand:
          accumulator.total_quantity_on_hand + row.total_quantity_on_hand,
        total_stock_value:
          accumulator.total_stock_value + row.total_stock_value,
        purchase_invoice_count:
          accumulator.purchase_invoice_count + row.purchase_invoice_count,
        total_purchase_value:
          accumulator.total_purchase_value + row.total_purchase_value,
        outstanding_payables:
          accumulator.outstanding_payables + row.outstanding_payables,
        active_suppliers: Math.max(
          accumulator.active_suppliers,
          row.active_suppliers,
        ),
        active_recipes: Math.max(
          accumulator.active_recipes,
          row.active_recipes,
        ),
      };
    },
    rows[0],
  );
};

export const fetchInventoryDashboardSnapshot = async ({
  companyId,
  outletId,
  dateFrom,
  dateTo,
}: InventoryDashboardFilterParams): Promise<
  ApiResponse<InventoryDashboardSnapshot>
> => {
  try {
    const overviewQuery = supabase
      .from('inventory_dashboard_overview_view')
      .select('*')
      .eq('company_id', companyId);

    const movementQuery = supabase
      .from('inventory_dashboard_movement_daily_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('metric_date', dateFrom)
      .lte('metric_date', dateTo)
      .order('metric_date', { ascending: true });

    const purchaseQuery = supabase
      .from('inventory_dashboard_purchase_daily_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('metric_date', dateFrom)
      .lte('metric_date', dateTo)
      .order('metric_date', { ascending: true });

    const lowStockQuery = supabase
      .from('inventory_dashboard_low_stock_view')
      .select('*')
      .eq('company_id', companyId)
      .order('deficit_quantity', { ascending: false })
      .limit(8);

    const locationValueQuery = supabase
      .from('inventory_dashboard_location_value_view')
      .select('*')
      .eq('company_id', companyId)
      .order('total_stock_value', { ascending: false })
      .limit(8);

    const recentActivityQuery = supabase
      .from('inventory_dashboard_recent_activity_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('occurred_at', formatTimestampStart(dateFrom))
      .lte('occurred_at', formatTimestampEnd(dateTo))
      .order('occurred_at', { ascending: false })
      .limit(4);

    const scopedQueries = outletId
      ? [
          overviewQuery.eq('outlet_id', outletId),
          movementQuery.eq('outlet_id', outletId),
          purchaseQuery.eq('outlet_id', outletId),
          lowStockQuery.eq('outlet_id', outletId),
          locationValueQuery.eq('outlet_id', outletId),
          recentActivityQuery.eq('outlet_id', outletId),
        ]
      : [
          overviewQuery,
          movementQuery,
          purchaseQuery,
          lowStockQuery,
          locationValueQuery,
          recentActivityQuery,
        ];

    const [
      overviewResult,
      movementsResult,
      purchasesResult,
      lowStockResult,
      locationValueResult,
      recentActivityResult,
    ] = await Promise.all(scopedQueries);

    const errors = [
      overviewResult.error,
      movementsResult.error,
      purchasesResult.error,
      lowStockResult.error,
      locationValueResult.error,
      recentActivityResult.error,
    ].filter(Boolean);

    if (errors.length) {
      return {
        success: false,
        error: errors[0]?.message ?? 'Failed to load inventory dashboard',
        data: undefined,
      };
    }

    const overviewRows = (overviewResult.data || []).map((row) =>
      mapOverviewRow(row as Record<string, unknown>),
    );
    const movementRows = (movementsResult.data || []).map((row) =>
      mapMovementDailyRow(row as Record<string, unknown>),
    );
    const purchaseRows = (purchasesResult.data || []).map((row) =>
      mapPurchaseDailyRow(row as Record<string, unknown>),
    );

    return {
      success: true,
      data: {
        overview: aggregateOverview(overviewRows),
        movements: fillMovementDailyRows(movementRows, dateFrom, dateTo),
        purchases: fillPurchaseDailyRows(purchaseRows, dateFrom, dateTo),
        lowStock: (lowStockResult.data || []).map((row) =>
          mapLowStockRow(row as Record<string, unknown>),
        ),
        locationValues: (locationValueResult.data || []).map((row) =>
          mapLocationValueRow(row as Record<string, unknown>),
        ),
        recentActivity: (recentActivityResult.data || []).map((row) =>
          mapRecentActivityRow(row as Record<string, unknown>),
        ),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory dashboard snapshot',
      data: undefined,
    };
  }
};

export interface FetchLowStockItemsParams {
  companyId: number;
  outletId?: string | null;
  stockLocationId?: string;
  search?: string;
  inventoryItemIds?: string[];
  page?: number;
  pageSize?: number;
}

export const fetchLowStockItems = async ({
  companyId,
  outletId,
  stockLocationId,
  search,
  inventoryItemIds,
  page = 1,
  pageSize = 50,
}: FetchLowStockItemsParams): Promise<
  ApiResponse<PaginatedResponse<InventoryDashboardLowStockRow>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('inventory_dashboard_low_stock_view')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('deficit_quantity', { ascending: true })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (stockLocationId && stockLocationId !== 'all') {
      query = query.eq('stock_location_id', stockLocationId);
    }

    if (inventoryItemIds?.length) {
      query = query.in('inventory_item_id', inventoryItemIds);
    }

    if (search) {
      query = query.ilike('item_name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count ?? 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []).map((row) =>
          mapLowStockRow(row as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to fetch low-stock items',
      data: undefined,
    };
  }
};

export const fetchLowStockItemExportRows = async (
  params: Omit<FetchLowStockItemsParams, 'page' | 'pageSize'>,
): Promise<ApiResponse<InventoryDashboardLowStockRow[]>> => {
  try {
    const rows: InventoryDashboardLowStockRow[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      // eslint-disable-next-line no-await-in-loop
      const result = await fetchLowStockItems({
        ...params,
        page,
        pageSize: 1000,
      });

      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch low-stock export rows',
          data: undefined,
        };
      }

      rows.push(...result.data.data);
      hasNextPage = result.data.pagination.hasNextPage;
      page += 1;

      if (!result.data.data.length) {
        break;
      }
    }

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch low-stock export rows',
      data: undefined,
    };
  }
};

export const fetchInventoryKpis = async (
  companyId: number,
  outletId?: string | null,
): Promise<ApiResponse<InventoryDashboardOverviewRow | null>> => {
  try {
    const overviewQuery = supabase
      .from('inventory_dashboard_overview_view')
      .select('*')
      .eq('company_id', companyId);

    const result = await (outletId
      ? overviewQuery.eq('outlet_id', outletId)
      : overviewQuery);

    if (result.error) {
      return { success: false, error: result.error.message, data: null };
    }

    const overviewRows = (result.data || []).map((row) =>
      mapOverviewRow(row as Record<string, unknown>),
    );

    return {
      success: true,
      data: aggregateOverview(overviewRows),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory KPIs',
      data: null,
    };
  }
};

export const fetchInventoryPerformance = async (
  companyId: number,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
): Promise<
  ApiResponse<{
    movements: InventoryDashboardMovementDailyRow[];
    purchases: InventoryDashboardPurchaseDailyRow[];
  }>
> => {
  try {
    let movementQuery = supabase
      .from('inventory_dashboard_movement_daily_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('metric_date', dateFrom)
      .lte('metric_date', dateTo)
      .order('metric_date', { ascending: true });

    let purchaseQuery = supabase
      .from('inventory_dashboard_purchase_daily_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('metric_date', dateFrom)
      .lte('metric_date', dateTo)
      .order('metric_date', { ascending: true });

    if (outletId) {
      movementQuery = movementQuery.eq('outlet_id', outletId);
      purchaseQuery = purchaseQuery.eq('outlet_id', outletId);
    }

    const [movementsResult, purchasesResult] = await Promise.all([
      movementQuery,
      purchaseQuery,
    ]);

    if (movementsResult.error || purchasesResult.error) {
      return {
        success: false,
        error: movementsResult.error?.message || purchasesResult.error?.message,
        data: undefined,
      };
    }

    const movementRows = (movementsResult.data || []).map((row) =>
      mapMovementDailyRow(row as Record<string, unknown>),
    );
    const purchaseRows = (purchasesResult.data || []).map((row) =>
      mapPurchaseDailyRow(row as Record<string, unknown>),
    );

    return {
      success: true,
      data: {
        movements: fillMovementDailyRows(movementRows, dateFrom, dateTo),
        purchases: fillPurchaseDailyRows(purchaseRows, dateFrom, dateTo),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch performance charts',
      data: undefined,
    };
  }
};

export const fetchInventoryLocationValues = async (
  companyId: number,
  outletId?: string | null,
): Promise<ApiResponse<InventoryDashboardLocationValueRow[]>> => {
  try {
    const locationValueQuery = supabase
      .from('inventory_dashboard_location_value_view')
      .select('*')
      .eq('company_id', companyId)
      .order('total_stock_value', { ascending: false })
      .limit(8);

    const result = await (outletId
      ? locationValueQuery.eq('outlet_id', outletId)
      : locationValueQuery);

    if (result.error) {
      return { success: false, error: result.error.message, data: undefined };
    }

    const rows = (result.data || []).map((row) =>
      mapLocationValueRow(row as Record<string, unknown>),
    );

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch location stock values',
      data: undefined,
    };
  }
};

export const fetchInventoryLowStock = async (
  companyId: number,
  outletId?: string | null,
): Promise<ApiResponse<InventoryDashboardLowStockRow[]>> => {
  try {
    const lowStockQuery = supabase
      .from('inventory_dashboard_low_stock_view')
      .select('*')
      .eq('company_id', companyId)
      .order('deficit_quantity', { ascending: false })
      .limit(8);

    const result = await (outletId
      ? lowStockQuery.eq('outlet_id', outletId)
      : lowStockQuery);

    if (result.error) {
      return { success: false, error: result.error.message, data: undefined };
    }

    const rows = (result.data || []).map((row) =>
      mapLowStockRow(row as Record<string, unknown>),
    );

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch low stock list',
      data: undefined,
    };
  }
};

export const fetchInventoryRecentActivity = async (
  companyId: number,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
): Promise<ApiResponse<InventoryDashboardRecentActivityRow[]>> => {
  try {
    let recentActivityQuery = supabase
      .from('inventory_dashboard_recent_activity_view')
      .select('*')
      .eq('company_id', companyId)
      .gte('occurred_at', formatTimestampStart(dateFrom))
      .lte('occurred_at', formatTimestampEnd(dateTo))
      .order('occurred_at', { ascending: false })
      .limit(4);

    if (outletId) {
      recentActivityQuery = recentActivityQuery.eq('outlet_id', outletId);
    }

    const result = await recentActivityQuery;

    if (result.error) {
      return { success: false, error: result.error.message, data: undefined };
    }

    const rows = (result.data || []).map((row) =>
      mapRecentActivityRow(row as Record<string, unknown>),
    );

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch recent activity',
      data: undefined,
    };
  }
};
